// src/pages/DocumentEdit.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, getFirestore } from 'firebase/firestore';
import { auth, db } from '../../services/firebase'; // Adjust path to your firebase config
import TiptapEditor from '../../components/TiptapEditor'; // Your editor component
import DOMPurify from 'dompurify'; // Already using for view, good to keep consistent

const DocumentEdit = () => {
//   const { docId } = useParams<{ docId: string }>();
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState(''); // HTML string
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load document on mount
  useEffect(() => {
    const fetchDocument = async () => {
      if (!docId) {
      setError('No document ID provided');
      setLoading(false);
      return;
    }
    
    const user = auth.currentUser;
    if (!user) {
        setError('You must be logged in to edit this document.');
        setLoading(false);
        navigate('/auth/signon'); // Redirect to login
        return;
    }

    console.log('[FETCH START] docId:', docId);
    console.log('[FETCH AUTH] currentUser UID:', auth?.currentUser?.uid || 'NO USER');

      try {
        console.log('[FETCH] Creating doc ref for collection "documents"');
        
        // Client-side check (Firestore rules will also enforce this)
        const idTokenResult = await user.getIdTokenResult(true);
        const isAdmin = idTokenResult.claims.admin === true;

        if (!user) {
            setError('You must be logged in to edit this document.');
            setLoading(false);
            navigate('/auth/signon'); // Redirect to login
            return;
        }

        console.log('[FETCH] Executing getDoc...');
        const docRef = doc(db, 'documents', docId); // Collection name: 'documents'
        const docSnap = await getDoc(docRef);
        console.log('[FETCH] getDoc returned, exists:', docSnap.exists());

        if (docSnap.exists()) {
          const data = docSnap.data();
          setTitle(data.title || 'Untitled');
          setContent(data.content || '<p>Start editing...</p>');

          // Further client-side authorization check
          if (data.authorId !== user.uid && !isAdmin) {
            setError('You do not have permission to edit this document.');
            setLoading(false);
            return;
          }
        } else {
          setError('Document not found (ID exists in URL but no data)');
        }
      } catch (err) {
        console.error('[FETCH ERROR]', err);
        if (err instanceof Error) {
          console.error('[FETCH ERROR details]', err.name, err.message, err.stack);
        } else {
          console.error('[FETCH ERROR details] Non-Error object:', err);
        }
        console.error('Error fetching document: ' + docId, err);
        setError('Failed to load document id is: ' + docId + '.');
      } finally {
        console.log('[FETCH] Finally block reached – setting loading false');
        setLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  const handleSave = async () => {

    if (!auth.currentUser) {
        setError('Sign in required');
        return;
    }
    if (!title.trim()) {
        setError('Title required');
        return;
    }

    setSaving(true);
    setError(null);

    try {
        const docRef = doc(db, 'documents', docId!);
        await updateDoc(docRef, {
            title: title.trim(),
            content: content,
            updatedAt: serverTimestamp(),
        });
        console.log('Updated doc');
        navigate(`/author/${docId}`);
    } catch (err: unknown) {
        console.error('Update failed:', err);
        const msg = err instanceof Error ? err.message : 'Unknown';
        setError(`Update failed: ${msg}`);
    } finally {
        setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto p-6">Loading document...</div>;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-red-600">
        {error}
        <button
          onClick={() => navigate('/author')}
          className="ml-4 text-blue-600 underline"
        >
          Back to Author
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Edit Document</h1>

      {/* Title input */}
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Document Title"
        className="w-full text-2xl font-semibold mb-6 p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {/* Tiptap Editor – loads existing content */}
      <TiptapEditor
        content={content}
        onChange={setContent} // Updates state on every change
        editable={true}
      />

      {error && <p className="text-red-600 mt-4">{error}</p>}

      <div className="mt-8 flex gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-3 rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        <button
          onClick={() => navigate(`/author/${docId}`)} // Or '/author' to cancel
          className="px-6 py-3 rounded bg-gray-300 hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DocumentEdit;